import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import {
  pmsCriticalityField,
  pmsCrewRankField,
  pmsDepartmentField,
  pmsEquipmentField,
  pmsIntervalUnitField,
  pmsJobTypeField,
  pmsMaintenanceCounterField,
  pmsMaintenanceStrategyField,
  pmsVerificationStatusField} from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsMaintenanceJob } from './useData';
import { ScheduleModePicker } from './ScheduleModePicker';

function toIsoDate(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') return value.slice(0, 10);
  return undefined;
}

function buildJobPayload(data: Partial<PmsMaintenanceJob>): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    equipmentId: data.equipmentId != null ? Number(data.equipmentId) : undefined,
    departmentId: data.departmentId != null ? Number(data.departmentId) : undefined,
    responsibleRankId: data.responsibleRankId != null ? Number(data.responsibleRankId) : undefined,
    secondaryRankId: data.secondaryRankId != null ? Number(data.secondaryRankId) : undefined,
    title: data.title,
    description: data.description,
    jobTypeId: data.jobTypeId != null ? Number(data.jobTypeId) : undefined,
    maintenanceStrategyId:
      data.maintenanceStrategyId != null ? Number(data.maintenanceStrategyId) : undefined,
    criticalityId: data.criticalityId != null ? Number(data.criticalityId) : undefined,
    verificationStatusId:
      data.verificationStatusId != null ? Number(data.verificationStatusId) : undefined,
    priority: data.priority != null ? Number(data.priority) : undefined,
    requiredPersonnel: data.requiredPersonnel != null ? Number(data.requiredPersonnel) : undefined,
    permitRequired: data.permitRequired ?? false,
    riskAssessmentRequired: data.riskAssessmentRequired ?? false,
    isActive: data.isActive ?? true};

  if (data.scheduleMode) {
    payload.primarySchedule = {
      scheduleMode: String(data.scheduleMode),
      counterId: data.counterId != null ? Number(data.counterId) : undefined,
      intervalValue: data.intervalValue != null ? Number(data.intervalValue) : undefined,
      intervalUnitId: data.intervalUnitId != null ? Number(data.intervalUnitId) : undefined,
      initialDueDate: toIsoDate(data.initialDueDate),
      lastCompletedDate: toIsoDate(data.lastCompletedDate),
      nextDueDate: toIsoDate(data.nextDueDate),
      lastCompletedCounter:
        data.lastCompletedCounter != null ? Number(data.lastCompletedCounter) : undefined,
      nextDueCounter: data.nextDueCounter != null ? Number(data.nextDueCounter) : undefined,
      warningBeforeValue:
        data.warningBeforeValue != null ? Number(data.warningBeforeValue) : undefined,
      warningBeforeUnitId:
        data.warningBeforeUnitId != null ? Number(data.warningBeforeUnitId) : undefined,
      gracePeriodValue: data.gracePeriodValue != null ? Number(data.gracePeriodValue) : undefined,
      gracePeriodUnitId: data.gracePeriodUnitId != null ? Number(data.gracePeriodUnitId) : undefined,
      isActive: true};
  }

  return payload;
}

export const moduleConfig = {
  module: {
    title: "Maintenance jobs",
    permissionModuleKey: 'pms_maintenance_jobs',
    route: {
      list: '/pms/pms-maintenance-jobs',
      new: '/pms/pms-maintenance-jobs/new',
      detail: (id: number) => `/pms/pms-maintenance-jobs/${id}`,
      edit: (id: number) => `/pms/pms-maintenance-jobs/${id}/edit`},
    api: { list: '/pms-maintenance-jobs', detail: (id: number) => `/pms-maintenance-jobs/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsMaintenanceJob[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-maintenance-jobs/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsMaintenanceJob) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsMaintenanceJob } = await import('../../../services/mutations');
      await deletePmsMaintenanceJob(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "Maintenance jobs",
    getTitle: (item: PmsMaintenanceJob) => item.title ?? `#${item.id}`},
  form: {
    new: { title: 'New maintenance job', submitText: 'Create' },
    edit: { title: 'Edit maintenance job', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-5xl',
    create: async (data: Partial<PmsMaintenanceJob>) => {
      const { createPmsMaintenanceJob } = await import('../../../services/mutations');
      return createPmsMaintenanceJob(buildJobPayload(data));
    },
    update: async (id: number, data: Partial<PmsMaintenanceJob>) => {
      const { updatePmsMaintenanceJob } = await import('../../../services/mutations');
      return updatePmsMaintenanceJob(id, buildJobPayload(data));
    }},
  fields: [
    pmsEquipmentField(true),
    pmsDepartmentField(true),
    pmsCrewRankField('responsibleRankId', 'Responsible rank', false),
    pmsCrewRankField('secondaryRankId', 'Secondary rank', false),
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'e.g. Main engine overhaul — cylinder head inspection',
      validation: { required: true }},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Scope, precautions, and references for the crew'},
    pmsJobTypeField(true),
    pmsMaintenanceStrategyField(true),
    pmsCriticalityField(true),
    pmsVerificationStatusField(true),
    { name: 'priority', label: 'Priority', type: 'number', placeholder: '1 = highest' },
    { name: 'requiredPersonnel', label: 'Crew required', type: 'number', min: 0 },
    {
      name: 'permitRequired',
      label: 'Permit',
      type: 'switch',
      switchLabel: 'Work permit required'},
    {
      name: 'riskAssessmentRequired',
      label: 'Risk assessment',
      type: 'switch',
      switchLabel: 'Risk assessment required'},
    { name: 'isActive', label: 'Status', type: 'switch', switchLabel: 'Job is active' },
    {
      name: 'scheduleMode',
      label: 'How is due calculated?',
      type: 'custom',
      required: true,
      validation: { required: true, message: 'Choose how due is calculated' },
      customRender: ({ value, onChange, error }) => (
        <ScheduleModePicker value={value} onChange={onChange} error={error} />
      )},
    {
      name: 'intervalValue',
      label: 'Every',
      type: 'number',
      min: 0,
      placeholder: 'Interval',
      helperText: 'How often the job becomes due after last completion'},
    pmsIntervalUnitField('intervalUnitId', 'Unit', false),
    {
      name: 'gracePeriodValue',
      label: 'Grace window',
      type: 'number',
      min: 0,
      helperText: 'Allowed overrun after due before it is overdue'},
    pmsIntervalUnitField('gracePeriodUnitId', 'Grace unit', false),
    {
      name: 'warningBeforeValue',
      label: 'Warn before',
      type: 'number',
      min: 0,
      helperText: 'Advance notice before the job becomes due'},
    pmsIntervalUnitField('warningBeforeUnitId', 'Warn unit', false),
    { name: 'initialDueDate', label: 'Initial due', type: 'date' },
    { name: 'lastCompletedDate', label: 'Last done', type: 'date' },
    {
      name: 'nextDueDate',
      label: 'Next due',
      type: 'date',
      readOnly: true,
      helperText: 'Calculated automatically'},
    {
      ...pmsMaintenanceCounterField(false),
      label: 'Depends on counter',
      helperText: 'Select the counter whose readings drive this job’s due status',
      validation: {
        requiredWhen: (fd) => fd.scheduleMode === 'RUNNING_HOURS',
        message: 'Select which counter this job depends on'}},
    {
      name: 'lastCompletedCounter',
      label: 'Last done (reading)',
      type: 'number',
      helperText: 'Counter value when the job was last completed'},
    {
      name: 'nextDueCounter',
      label: 'Next due (reading)',
      type: 'number',
      readOnly: true,
      helperText: 'Calculated automatically from last done + interval'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'assignment',
        title: 'Assignment',
        description: 'Equipment this job belongs to, and who owns it on board.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['equipmentId', 'departmentId', 'responsibleRankId', 'secondaryRankId'],
        fieldSpans: {
          equipmentId: 'md:col-span-12',
          departmentId: 'md:col-span-4',
          responsibleRankId: 'md:col-span-4',
          secondaryRankId: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'job',
        title: 'Job details',
        description: 'What to do and how the job is classified.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'title',
          'description',
          'jobTypeId',
          'maintenanceStrategyId',
          'criticalityId',
          'verificationStatusId',
          'priority',
          'requiredPersonnel',
        ],
        fieldSpans: {
          title: 'md:col-span-12',
          description: 'md:col-span-12',
          jobTypeId: 'md:col-span-3',
          maintenanceStrategyId: 'md:col-span-3',
          criticalityId: 'md:col-span-3',
          verificationStatusId: 'md:col-span-3',
          priority: 'md:col-span-3',
          requiredPersonnel: 'md:col-span-3'},
        collapsible: false},
      {
        id: 'requirements',
        title: 'Requirements & status',
        description: 'Safety gates and whether the job is in the active PMS plan.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['permitRequired', 'riskAssessmentRequired', 'isActive'],
        fieldSpans: {
          permitRequired: 'md:col-span-4',
          riskAssessmentRequired: 'md:col-span-4',
          isActive: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'scheduling',
        title: 'Scheduling',
        description: 'Pick one mode — the fields below adapt to your choice.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['scheduleMode'],
        fieldSpans: { scheduleMode: 'md:col-span-12' },
        collapsible: false},
      {
        id: 'scheduling-calendar',
        title: 'Calendar interval',
        description: 'Due dates move forward by calendar period after each completion.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'intervalValue',
          'intervalUnitId',
          'gracePeriodValue',
          'gracePeriodUnitId',
          'warningBeforeValue',
          'warningBeforeUnitId',
          'initialDueDate',
          'lastCompletedDate',
          'nextDueDate',
        ],
        fieldSpans: {
          intervalValue: 'md:col-span-3',
          intervalUnitId: 'md:col-span-3',
          gracePeriodValue: 'md:col-span-3',
          gracePeriodUnitId: 'md:col-span-3',
          warningBeforeValue: 'md:col-span-3',
          warningBeforeUnitId: 'md:col-span-3',
          initialDueDate: 'md:col-span-4',
          lastCompletedDate: 'md:col-span-4',
          nextDueDate: 'md:col-span-4'},
        collapsible: false,
        visibleWhen: (fd) => fd.scheduleMode === 'CALENDAR',
        visibilityDependsOn: ['scheduleMode']},
      {
        id: 'scheduling-counter',
        title: 'Counter interval',
        description: 'Link a maintenance counter; due advances when its reading reaches the next threshold.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'counterId',
          'intervalValue',
          'intervalUnitId',
          'warningBeforeValue',
          'warningBeforeUnitId',
          'lastCompletedCounter',
          'nextDueCounter',
        ],
        fieldSpans: {
          counterId: 'md:col-span-12',
          intervalValue: 'md:col-span-3',
          intervalUnitId: 'md:col-span-3',
          warningBeforeValue: 'md:col-span-3',
          warningBeforeUnitId: 'md:col-span-3',
          lastCompletedCounter: 'md:col-span-6',
          nextDueCounter: 'md:col-span-6'},
        collapsible: false,
        visibleWhen: (fd) => fd.scheduleMode === 'RUNNING_HOURS',
        visibilityDependsOn: ['scheduleMode']},
      {
        id: 'scheduling-condition',
        title: 'Observation-based',
        description:
          'No fixed interval. Create work from inspections or findings when the condition warrants maintenance.',
        icon: '',
        fields: [],
        collapsible: false,
        visibleWhen: (fd) => fd.scheduleMode === 'CONDITION',
        visibilityDependsOn: ['scheduleMode']},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const codeName = (
  rel: { code?: string | null; name?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelEquipment = (item: PmsMaintenanceJob) => {
  return item.equipment?.name ?? (item.equipmentId != null ? `#${item.equipmentId}` : '-');
};

const labelRank = (
  rel: { code?: string | null; name?: string | null; abrv?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.abrv ?? rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelDue = (item: PmsMaintenanceJob, formatDate: (s?: string) => string) => {
  const s = item.primarySchedule;
  if (!s) return '-';
  const mode = s.scheduleType?.code ?? '-';
  if (mode === 'CONDITION') return 'Observation';
  if (s.nextDueDate) return formatDate(String(s.nextDueDate));
  if (s.nextDueCounter != null) return `${s.nextDueCounter}${s.counter?.unit ? ` ${s.counter.unit}` : ''}`;
  return mode;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PmsMaintenanceJob>[] => [
  { key: 'title', header: 'Title', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'equipmentId',
    header: 'Equipment',
    sortable: true,
    render: (_v, row) => <TextCell value={labelEquipment(row)} />},
  {
    key: 'dueStatus',
    header: 'Next due',
    sortable: false,
    render: (_v, row) => <TextCell value={labelDue(row, formatDate)} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsMaintenanceJob, formatDate: (s?: string) => string) => {
  const s = item.primarySchedule;
  const scheduleFields = s
    ? [
        { label: 'Schedule mode', value: codeName(s.scheduleType, s.scheduleTypeId) },
        { label: 'Due status', value: item.dueStatus ?? '-' },
        { label: 'Counter', value: s.counter?.name ?? (s.counterId != null ? `#${s.counterId}` : '-') },
        {
          label: 'Frequency',
          value:
            s.intervalValue != null
              ? `${s.intervalValue}${s.intervalUnit ? ` ${s.intervalUnit.name ?? s.intervalUnit.code}` : ''}`
              : '-'},
        {
          label: 'Window (grace)',
          value:
            s.gracePeriodValue != null
              ? `${s.gracePeriodValue}${s.gracePeriodUnit ? ` ${s.gracePeriodUnit.name ?? s.gracePeriodUnit.code}` : ''}`
              : '-'},
        { label: 'Last done', value: s.lastCompletedDate ? formatDate(String(s.lastCompletedDate)) : '-' },
        { label: 'Next due', value: s.nextDueDate ? formatDate(String(s.nextDueDate)) : '-' },
        {
          label: 'Last done (counter)',
          value: s.lastCompletedCounter != null ? String(s.lastCompletedCounter) : '-'},
        {
          label: 'Next due (counter)',
          value: s.nextDueCounter != null ? String(s.nextDueCounter) : '-'},
      ]
    : [{ label: 'Schedule', value: 'None' }];

  return [
    {
      title: 'Overview',
      fields: [
        { label: 'Equipment', value: labelEquipment(item) },
        { label: 'Department', value: codeName(item.department, item.departmentId) },
        { label: 'Responsible rank', value: labelRank(item.responsibleRank, item.responsibleRankId) },
        { label: 'Secondary rank', value: labelRank(item.secondaryRank, item.secondaryRankId) },
        { label: 'Title', value: item.title != null ? String(item.title) : '-' },
        { label: 'Description', value: item.description != null ? String(item.description) : '-' },
        { label: 'Job type', value: codeName(item.jobType, item.jobTypeId) },
        { label: 'Strategy', value: codeName(item.maintenanceStrategy, item.maintenanceStrategyId) },
        { label: 'Criticality', value: codeName(item.criticality, item.criticalityId) },
        { label: 'Verification', value: codeName(item.verificationStatus, item.verificationStatusId) },
        { label: 'Priority', value: item.priority != null ? String(item.priority) : '-' },
        { label: 'Required personnel', value: item.requiredPersonnel != null ? String(item.requiredPersonnel) : '-' },
        { label: 'Permit required', value: item.permitRequired ? 'Yes' : 'No' },
        { label: 'Risk assessment required', value: item.riskAssessmentRequired ? 'Yes' : 'No' },
        { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
        { label: 'Created', value: formatDate(item.createdAt) },
      ]},
    {
      title: 'Scheduling',
      fields: scheduleFields},
  ];
};

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsMaintenanceJob> => ({
  equipmentId: undefined,
  departmentId: undefined,
  responsibleRankId: undefined,
  secondaryRankId: undefined,
  title: '',
  description: '',
  jobTypeId: undefined,
  maintenanceStrategyId: undefined,
  criticalityId: undefined,
  verificationStatusId: undefined,
  priority: undefined,
  requiredPersonnel: undefined,
  permitRequired: false,
  riskAssessmentRequired: false,
  isActive: true,
  scheduleMode: undefined,
  counterId: undefined,
  intervalValue: undefined,
  intervalUnitId: undefined,
  initialDueDate: undefined,
  lastCompletedDate: undefined,
  nextDueDate: undefined,
  lastCompletedCounter: undefined,
  nextDueCounter: undefined,
  warningBeforeValue: undefined,
  warningBeforeUnitId: undefined,
  gracePeriodValue: undefined,
  gracePeriodUnitId: undefined});

export const mapItemToFormData = (item: PmsMaintenanceJob): Partial<PmsMaintenanceJob> => {
  const s = item.primarySchedule;
  return {
    equipmentId: item.equipmentId != null ? Number(item.equipmentId) : undefined,
    departmentId: item.departmentId != null ? Number(item.departmentId) : undefined,
    responsibleRankId: item.responsibleRankId != null ? Number(item.responsibleRankId) : undefined,
    secondaryRankId: item.secondaryRankId != null ? Number(item.secondaryRankId) : undefined,
    title: item.title ?? '',
    description: item.description ?? '',
    jobTypeId: item.jobTypeId != null ? Number(item.jobTypeId) : undefined,
    maintenanceStrategyId: item.maintenanceStrategyId != null ? Number(item.maintenanceStrategyId) : undefined,
    criticalityId: item.criticalityId != null ? Number(item.criticalityId) : undefined,
    verificationStatusId: item.verificationStatusId != null ? Number(item.verificationStatusId) : undefined,
    priority: item.priority != null ? Number(item.priority) : undefined,
    requiredPersonnel: item.requiredPersonnel != null ? Number(item.requiredPersonnel) : undefined,
    permitRequired: item.permitRequired ?? false,
    riskAssessmentRequired: item.riskAssessmentRequired ?? false,
    isActive: item.isActive ?? true,
    scheduleMode: s?.scheduleType?.code ?? undefined,
    counterId: s?.counterId != null ? Number(s.counterId) : undefined,
    intervalValue: s?.intervalValue != null ? Number(s.intervalValue) : undefined,
    intervalUnitId: s?.intervalUnitId != null ? Number(s.intervalUnitId) : undefined,
    initialDueDate: s?.initialDueDate ? String(s.initialDueDate).slice(0, 10) : undefined,
    lastCompletedDate: s?.lastCompletedDate ? String(s.lastCompletedDate).slice(0, 10) : undefined,
    nextDueDate: s?.nextDueDate ? String(s.nextDueDate).slice(0, 10) : undefined,
    lastCompletedCounter: s?.lastCompletedCounter != null ? Number(s.lastCompletedCounter) : undefined,
    nextDueCounter: s?.nextDueCounter != null ? Number(s.nextDueCounter) : undefined,
    warningBeforeValue: s?.warningBeforeValue != null ? Number(s.warningBeforeValue) : undefined,
    warningBeforeUnitId: s?.warningBeforeUnitId != null ? Number(s.warningBeforeUnitId) : undefined,
    gracePeriodValue: s?.gracePeriodValue != null ? Number(s.gracePeriodValue) : undefined,
    gracePeriodUnitId: s?.gracePeriodUnitId != null ? Number(s.gracePeriodUnitId) : undefined};
};

export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
